import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DataTable from "../components/common/DataTable";
import api from "../services/api";
import { showToast, confirmDelete } from "../helper/notifications"; // UPDATED IMPORTS
import { useDebounce } from "../hook/useDebounce";

const LocationsMainListPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const activeAreaId = state?.filterAreaId || null;
  const activeAreaName = state?.filterAreaName || null;

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  /* ===== Sorting State ===== */
  const [sortConfig, setSortConfig] = useState({ key: "location_name", direction: "asc" });
  
  const debouncedSearch = useDebounce(search, 500);

  // Wrapped in useCallback for consistency with your other pages
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/main-locations", {
        params: {
          search: debouncedSearch,
          page,
          limit,
          sort_by: sortConfig.key,
          order: sortConfig.direction,
          ...(activeAreaId && { top_location_id: activeAreaId }),
        },
      });

      setData(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch {
      showToast("error", "Failed to load main locations"); // UPDATED
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, activeAreaId, sortConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      {activeAreaId && (
        <div className="mb-12 px-10">
          <span
            className="badge bg-primary-100 text-primary-600 px-12 py-8 radius-8 pointer d-inline-flex align-items-center gap-2"
            onClick={() => navigate("/locations-main", { replace: true })}
          >
            Filtered by Top Area: {activeAreaName} 
            <i className="ri-close-line"></i>
          </span>
        </div>
      )}

      <DataTable
        title="Main Locations"
        data={data}
        loading={loading}
        searchTerm={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => {
          setLimit(v);
          setPage(1);
        }}
        showCheckbox={false}
        sortConfig={sortConfig}
        onSortChange={(config) => {
          setSortConfig(config);
          setPage(1);
        }}

        headerActions={[
          {
            label: "Add Business",
            icon: "ri-add-line",
            className: "btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2",
            onClick: () => navigate("/locations-main/new"),
          },
        ]}

        columns={[
          {
            key: "location_name",
            label: "Name",
            className: "text-md fw-semibold",
            sortable: true,
            render: (row) => (
              <span
                className="text-primary-600 pointer hover-text-decoration-underline"
                onClick={() =>
                  navigate("/locations-sub", {
                    state: {
                      filterBusinessId: row.id,
                      filterBusinessName: row.location_name,
                    },
                  })
                }
              >
                {row.location_name}
              </span>
            ),
          },
          { 
            key: "top_location_name", 
            label: "Parent Area", 
            className: "text-secondary-light fw-medium",
            sortable: true 
          },
          { 
            key: "city", 
            label: "City", 
            className: "text-secondary-light fw-medium",
            sortable: true 
          },
          {
            key: "sync_status",
            label: "Status",
            sortable: true,
            render: (row) => (
              <span
                className={`badge px-24 py-4 radius-4 fw-medium text-sm ${
                  row.sync_status === "SYNCED"
                    ? "bg-success-focus text-success-main"
                    : "bg-warning-focus text-warning-main"
                }`}
              >
                {row.sync_status}
              </span>
            ),
          },
        ]}

        actions={[
          {
            icon: "lucide:edit",
            className: "w-32-px h-32-px bg-success-focus text-success-main rounded d-flex align-items-center justify-content-center border-0",
            onClick: (row) => navigate(`/locations-main/${row.location_id}/edit`),
          },
          {
            icon: "mingcute:delete-2-line",
            className: "w-32-px h-32-px bg-danger-focus text-danger-main rounded d-flex align-items-center justify-content-center border-0",
            onClick: (row) => {
              // REPLACED window.confirm with confirmDelete helper
              confirmDelete(row.location_name, async () => {
                try {
                  await api.delete(`/main-locations/${row.location_id}`);
                  showToast("success", "Main location deleted"); // UPDATED
                  fetchData();
                } catch {
                  showToast("error", "Delete failed"); // UPDATED
                }
              });
            },
          },
        ]}
      />
    </>
  );
};

export default LocationsMainListPage;